import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Store as VenueIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import venueService from '../../../services/venueService';
import type { Venue, VenueType, VenueStatus } from '../../../types/venue';

const VENUE_TYPES: VenueType[] = ['RESTAURANT', 'HOTEL', 'WINE_BAR', 'RETAIL', 'PRIVATE'];
const VENUE_STATUSES: VenueStatus[] = ['ACTIVE', 'INACTIVE'];

const statusColor = (status: VenueStatus): 'success' | 'default' =>
  status === 'ACTIVE' ? 'success' : 'default';

const VenueList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [items, setItems] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
  });
  const [sortField] = useState('name');
  const [sortOrder] = useState<'asc' | 'desc'>('asc');

  const fetchItems = useCallback(async (query: string, type: string, status: string, page: number, pageSize: number) => {
    setLoading(true);
    try {
      const result = await venueService.getAllVenues(
        page + 1,
        pageSize,
        sortField,
        sortOrder,
        query || undefined,
        type || undefined,
        status || undefined,
      );
      setItems(result.data);
      setPagination(prev => ({
        ...prev,
        page,
        pageSize,
        totalItems: result.pagination.totalItems,
        totalPages: result.pagination.totalPages,
      }));
    } catch (err) {
      console.error('Failed to load venues', err);
    } finally {
      setLoading(false);
    }
  }, [sortField, sortOrder]);

  const debouncedFetch = useMemo(
    () => debounce((query: string, type: string, status: string, page: number, pageSize: number) => {
      fetchItems(query, type, status, page, pageSize);
    }, 300),
    [fetchItems],
  );

  useEffect(() => {
    debouncedFetch(searchQuery, typeFilter, statusFilter, pagination.page, pagination.pageSize);
    return () => debouncedFetch.cancel();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, typeFilter, statusFilter, pagination.page, pagination.pageSize]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleDelete = async (e: React.MouseEvent, venue: Venue) => {
    e.stopPropagation();
    if (!window.confirm(`Deactivate "${venue.name}"?`)) return;
    try {
      await venueService.deleteVenue(venue._id);
      fetchItems(searchQuery, typeFilter, statusFilter, pagination.page, pagination.pageSize);
    } catch (err) {
      console.error('Failed to delete venue', err);
    }
  };

  const thStyle = {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <VenueIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>Venues</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/venues/create')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            New Venue
          </Button>
        </Box>

        {/* Filters */}
        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <TextField
            size="small"
            placeholder="Search venues…"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={e => { setTypeFilter(e.target.value); setPagination(prev => ({ ...prev, page: 0 })); }}
            >
              <MenuItem value="">All types</MenuItem>
              {VENUE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={e => { setStatusFilter(e.target.value); setPagination(prev => ({ ...prev, page: 0 })); }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {VENUE_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={thStyle}>Name</TableCell>
                <TableCell sx={thStyle}>Type</TableCell>
                <TableCell sx={thStyle}>City</TableCell>
                <TableCell sx={thStyle}>Currency</TableCell>
                <TableCell sx={thStyle}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <VenueIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                      <Typography variant="body2">No venues found.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                items.map(venue => (
                  <TableRow
                    key={venue._id}
                    hover
                    onClick={() => navigate(`/admin/venues/${venue._id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{venue.name}</Typography>
                      {venue.shortName && (
                        <Typography variant="caption" color="text.secondary">{venue.shortName}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{venue.type}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{venue.city || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{venue.defaultCurrency}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={venue.status}
                        size="small"
                        color={statusColor(venue.status)}
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => navigate(`/admin/venues/edit/${venue._id}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={e => handleDelete(e, venue)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination.totalItems}
          page={pagination.page}
          rowsPerPage={pagination.pageSize}
          onPageChange={(_e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          onRowsPerPageChange={e => setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default VenueList;
